import { create } from 'zustand'
import { derivePathFromTiles } from '../game/model/derivePath'
import {
  authoredGroundRouteCount,
  normalizeMapRoutes,
  routeMovementClass
} from '../game/model/path'
import { getMap } from '../game/model/mapCatalog'
import { validateMap, type MapValidationError } from '../game/model/validateMap'
import type { GridPosition, MapDefinition, WaveDefinition, WaveGroupDefinition } from '../game/model/types'
import {
  cloneMap,
  createBlankMap,
  EDITOR_MAX_SIZE,
  EDITOR_MIN_SIZE,
  HISTORY_LIMIT,
  mapFingerprint,
  resizeMap,
  setTile,
  type EditorTool
} from './editorTypes'
import { isValidMapId, normalizeMapId } from './editorGuards'
import {
  createEmptyGroup,
  createEmptyWave,
  duplicateWave,
  moveItem
} from './waveHelpers'

export type EditorSection = 'map' | 'waves' | 'validation'

interface EditorState {
  map: MapDefinition
  baselineJson: string
  tool: EditorTool
  section: EditorSection
  hover: GridPosition | null
  past: MapDefinition[]
  future: MapDefinition[]
  sourceLabel: string

  isDirty: () => boolean
  canUndo: () => boolean
  canRedo: () => boolean
  validationErrors: () => MapValidationError[]
  isValid: () => boolean
  derivedPath: () => GridPosition[]

  newMap: (opts?: Parameters<typeof createBlankMap>[0]) => void
  loadBuiltin: (mapId: string) => boolean
  loadMap: (map: MapDefinition, sourceLabel: string) => void
  setTool: (tool: EditorTool) => void
  setSection: (section: EditorSection) => void
  setHover: (cell: GridPosition | null) => void
  setMeta: (patch: Partial<Pick<MapDefinition, 'id' | 'name' | 'startingGold' | 'startingLives'>>) => void
  setDimensions: (width: number, height: number) => void
  beginPaintStroke: () => void
  paint: (x: number, y: number) => void
  endPaintStroke: () => void

  addWave: () => void
  deleteWave: (waveIndex: number) => void
  moveWave: (waveIndex: number, direction: -1 | 1) => void
  duplicateWaveAt: (waveIndex: number) => void
  updateWave: (waveIndex: number, patch: Partial<Pick<WaveDefinition, 'id' | 'completionReward'>>) => void
  addGroup: (waveIndex: number) => void
  deleteGroup: (waveIndex: number, groupIndex: number) => void
  moveGroup: (waveIndex: number, groupIndex: number, direction: -1 | 1) => void
  updateGroup: (
    waveIndex: number,
    groupIndex: number,
    patch: Partial<WaveGroupDefinition>
  ) => void

  undo: () => void
  redo: () => void
  markSaved: () => void
}

/** Snapshot taken at stroke start; null when not painting. */
let strokeBase: MapDefinition | null = null

function pushHistory(past: MapDefinition[], snapshot: MapDefinition): MapDefinition[] {
  const next = [...past, snapshot]
  if (next.length > HISTORY_LIMIT) next.shift()
  return next
}

/** Multi-lane maps cannot be described by tile derivation (branching PATH). */
function isMultiLane(map: MapDefinition): boolean {
  return authoredGroundRouteCount(map) > 1
}

/**
 * Keep `path` in sync with painted tiles for single-lane maps, mirroring the
 * result into routes[] when the map carries one. Multi-lane maps are passed
 * through untouched so the editor cannot wipe lanes it has no UI for.
 */
function withDerivedPath(map: MapDefinition): MapDefinition {
  if (isMultiLane(map)) return map
  const derived = derivePathFromTiles(map)
  const next: MapDefinition = {
    ...map,
    path: derived.ok ? derived.path.map((c) => ({ ...c })) : []
  }
  if (map.routes) {
    next.routes = normalizeMapRoutes({
      ...next,
      routes: map.routes.filter((route) => routeMovementClass(route) === 'AIR')
    })
  }
  return next
}

function commitMap(
  set: (partial: Partial<EditorState>) => void,
  get: () => EditorState,
  nextMap: MapDefinition
): void {
  const state = get()
  if (mapFingerprint(nextMap) === mapFingerprint(state.map)) return
  set({
    map: withDerivedPath(nextMap),
    past: pushHistory(state.past, cloneMap(state.map)),
    future: []
  })
}

export const useMapEditorStore = create<EditorState>((set, get) => {
  const initial = withDerivedPath(createBlankMap())
  return {
    map: initial,
    baselineJson: mapFingerprint(initial),
    tool: 'path',
    section: 'map',
    hover: null,
    past: [],
    future: [],
    sourceLabel: 'new',

    isDirty: () => mapFingerprint(get().map) !== get().baselineJson,
    canUndo: () => get().past.length > 0,
    canRedo: () => get().future.length > 0,
    derivedPath: () => {
      const result = derivePathFromTiles(get().map)
      return result.ok ? result.path : []
    },
    validationErrors: () => {
      const raw = get().map
      const dimErrors: MapValidationError[] = []
      if (
        !Number.isInteger(raw.width) ||
        raw.width < EDITOR_MIN_SIZE ||
        raw.width > EDITOR_MAX_SIZE
      ) {
        dimErrors.push({
          rule: 'dimensions',
          message: `width must be an integer ${EDITOR_MIN_SIZE}..${EDITOR_MAX_SIZE}`
        })
      }
      if (
        !Number.isInteger(raw.height) ||
        raw.height < EDITOR_MIN_SIZE ||
        raw.height > EDITOR_MAX_SIZE
      ) {
        dimErrors.push({
          rule: 'dimensions',
          message: `height must be an integer ${EDITOR_MIN_SIZE}..${EDITOR_MAX_SIZE}`
        })
      }
      if (!Number.isInteger(raw.startingGold) || raw.startingGold < 0) {
        dimErrors.push({
          rule: 'starting-gold',
          message: 'startingGold must be an integer >= 0'
        })
      }
      if (!Number.isInteger(raw.startingLives) || raw.startingLives < 1) {
        dimErrors.push({
          rule: 'starting-lives',
          message: 'startingLives must be an integer >= 1'
        })
      }
      if (!isValidMapId(raw.id)) {
        dimErrors.push({
          rule: 'map-id',
          message:
            'id must be lowercase a-z, 0-9, hyphen only (no spaces or path segments)'
        })
      }
      if (dimErrors.length > 0) return dimErrors

      if (!isMultiLane(raw)) {
        const derived = derivePathFromTiles(raw)
        if (!derived.ok) return derived.errors
      }

      const map = withDerivedPath(raw)
      const validated = validateMap(map)
      return validated.ok ? [] : validated.errors
    },
    isValid: () => get().validationErrors().length === 0,

    newMap: (opts) => {
      const map = withDerivedPath(createBlankMap(opts))
      set({
        map,
        baselineJson: mapFingerprint(map),
        past: [],
        future: [],
        sourceLabel: 'new',
        hover: null,
        section: 'map'
      })
    },

    loadBuiltin: (mapId) => {
      const builtin = getMap(mapId)
      if (!builtin) return false
      const map = withDerivedPath(cloneMap(builtin))
      set({
        map,
        baselineJson: mapFingerprint(map),
        past: [],
        future: [],
        sourceLabel: `builtin:${mapId}`,
        hover: null
      })
      return true
    },

    loadMap: (incoming, sourceLabel) => {
      const map = withDerivedPath(cloneMap(incoming))
      set({
        map,
        baselineJson: mapFingerprint(map),
        past: [],
        future: [],
        sourceLabel,
        hover: null
      })
    },

    setTool: (tool) => set({ tool }),
    setSection: (section) => set({ section }),
    setHover: (cell) => set({ hover: cell }),

    setMeta: (patch) => {
      const state = get()
      const nextPatch = { ...patch }
      if (typeof nextPatch.id === 'string') {
        nextPatch.id = normalizeMapId(nextPatch.id) || nextPatch.id
      }
      commitMap(set, get, { ...state.map, ...nextPatch })
    },

    setDimensions: (width, height) => {
      if (
        !Number.isInteger(width) ||
        !Number.isInteger(height) ||
        width < EDITOR_MIN_SIZE ||
        height < EDITOR_MIN_SIZE ||
        width > EDITOR_MAX_SIZE ||
        height > EDITOR_MAX_SIZE
      ) {
        return
      }
      const state = get()
      commitMap(set, get, resizeMap(state.map, width, height))
    },

    beginPaintStroke: () => {
      strokeBase = cloneMap(get().map)
    },

    endPaintStroke: () => {
      const state = get()
      if (!strokeBase) return
      if (mapFingerprint(strokeBase) !== mapFingerprint(state.map)) {
        set({
          past: pushHistory(state.past, strokeBase),
          future: []
        })
      }
      strokeBase = null
    },

    paint: (x, y) => {
      const state = get()
      const { map, tool } = state
      if (x < 0 || y < 0 || x >= map.width || y >= map.height) return

      let nextRows = map.rows
      let spawn = map.spawn
      let exit = map.exit

      if (tool === 'grass') {
        nextRows = setTile(map.rows, x, y, 'G')
      } else if (tool === 'path') {
        nextRows = setTile(map.rows, x, y, 'P')
      } else if (tool === 'spawn') {
        nextRows = setTile(map.rows, x, y, 'P')
        spawn = { x, y }
      } else if (tool === 'exit') {
        nextRows = setTile(map.rows, x, y, 'P')
        exit = { x, y }
      }

      const candidate = withDerivedPath({
        ...map,
        rows: nextRows,
        spawn,
        exit
      })
      if (mapFingerprint(candidate) === mapFingerprint(map)) return

      if (strokeBase) {
        set({ map: candidate })
        return
      }

      set({
        map: candidate,
        past: pushHistory(state.past, cloneMap(map)),
        future: []
      })
    },

    addWave: () => {
      const state = get()
      const waves = [...state.map.waves, createEmptyWave(state.map.waves)]
      commitMap(set, get, { ...state.map, waves })
    },

    deleteWave: (waveIndex) => {
      const state = get()
      if (waveIndex < 0 || waveIndex >= state.map.waves.length) return
      const waves = state.map.waves.filter((_, i) => i !== waveIndex)
      commitMap(set, get, { ...state.map, waves })
    },

    moveWave: (waveIndex, direction) => {
      const state = get()
      const waves = moveItem(state.map.waves, waveIndex, waveIndex + direction)
      commitMap(set, get, { ...state.map, waves })
    },

    duplicateWaveAt: (waveIndex) => {
      const state = get()
      const wave = state.map.waves[waveIndex]
      if (!wave) return
      const copy = duplicateWave(wave, state.map.waves)
      const waves = [
        ...state.map.waves.slice(0, waveIndex + 1),
        copy,
        ...state.map.waves.slice(waveIndex + 1)
      ]
      commitMap(set, get, { ...state.map, waves })
    },

    updateWave: (waveIndex, patch) => {
      const state = get()
      const waves = state.map.waves.map((wave, i) =>
        i === waveIndex ? { ...wave, ...patch } : wave
      )
      commitMap(set, get, { ...state.map, waves })
    },

    addGroup: (waveIndex) => {
      const state = get()
      const waves = state.map.waves.map((wave, i) =>
        i === waveIndex
          ? { ...wave, groups: [...wave.groups, createEmptyGroup()] }
          : wave
      )
      commitMap(set, get, { ...state.map, waves })
    },

    deleteGroup: (waveIndex, groupIndex) => {
      const state = get()
      const waves = state.map.waves.map((wave, i) => {
        if (i !== waveIndex) return wave
        return {
          ...wave,
          groups: wave.groups.filter((_, gi) => gi !== groupIndex)
        }
      })
      commitMap(set, get, { ...state.map, waves })
    },

    moveGroup: (waveIndex, groupIndex, direction) => {
      const state = get()
      const waves = state.map.waves.map((wave, i) => {
        if (i !== waveIndex) return wave
        return {
          ...wave,
          groups: moveItem(wave.groups, groupIndex, groupIndex + direction)
        }
      })
      commitMap(set, get, { ...state.map, waves })
    },

    updateGroup: (waveIndex, groupIndex, patch) => {
      const state = get()
      const waves = state.map.waves.map((wave, i) => {
        if (i !== waveIndex) return wave
        return {
          ...wave,
          groups: wave.groups.map((group, gi) =>
            gi === groupIndex ? { ...group, ...patch } : group
          )
        }
      })
      commitMap(set, get, { ...state.map, waves })
    },

    undo: () => {
      const state = get()
      if (state.past.length === 0) return
      const previous = state.past[state.past.length - 1]
      const past = state.past.slice(0, -1)
      set({
        map: cloneMap(previous),
        past,
        future: [cloneMap(state.map), ...state.future].slice(0, HISTORY_LIMIT)
      })
    },

    redo: () => {
      const state = get()
      if (state.future.length === 0) return
      const [next, ...rest] = state.future
      set({
        map: cloneMap(next),
        past: pushHistory(state.past, cloneMap(state.map)),
        future: rest
      })
    },

    markSaved: () => {
      set({ baselineJson: mapFingerprint(get().map) })
    }
  }
})
